// File: frontend/src/pages/branding/BrandingSettings.jsx

import React, { useState, useEffect, useRef } from 'react';
import api, { getImageUrl } from '../../utils/axios';
import DashboardLayout from '../../components/DashboardLayout';
import { useSchool } from '../../context/SchoolContext';

const BrandingSettings = ({ embedded = false }) => {
  const { school, reloadSchoolSettings } = useSchool();
  const [activeTab, setActiveTab] = useState('colors');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const logoInputRef = useRef(null);
  const stampInputRef = useRef(null);
  const sigInputRef = useRef(null);

  const handleUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('logo', file);
      const res = await api.post('/api/v1/settings/logo', fd);
      if (res.data?.success) {
        setFeedback({ type: 'success', message: 'School logo uploaded successfully!' });
        if (reloadSchoolSettings) reloadSchoolSettings();
      }
    } catch (err) {
      setFeedback({ type: 'error', message: err.response?.data?.message || 'Failed to upload logo.' });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadStamp = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('stamp', file);
      const res = await api.post('/api/v1/settings/stamp', fd);
      if (res.data?.success) {
        setFeedback({ type: 'success', message: 'School stamp uploaded successfully!' });
        if (reloadSchoolSettings) reloadSchoolSettings();
      }
    } catch (err) {
      setFeedback({ type: 'error', message: err.response?.data?.message || 'Failed to upload stamp.' });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadSignature = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('signature', file);
      const res = await api.post('/api/v1/settings/signature', fd);
      if (res.data?.success) {
        setFeedback({ type: 'success', message: 'Principal signature uploaded successfully!' });
        if (reloadSchoolSettings) reloadSchoolSettings();
      }
    } catch (err) {
      setFeedback({ type: 'error', message: err.response?.data?.message || 'Failed to upload signature.' });
    } finally {
      setUploading(false);
    }
  };

  const isValidImage = (url) => {
    if (!url || typeof url !== 'string') return false;
    const clean = url.trim();
    if (clean === '0' || clean === '1' || clean === 'null' || clean === 'undefined' || clean === 'none') return false;
    return clean.length > 3;
  };

  // State: Color Tokens & Theme
  const [colors, setColors] = useState({
    primary: '#0F172A',
    secondary: '#1E293B',
    tertiary: '#10B981',
    text: '#1E293B',
    button: '#2563EB',
    link: '#2563EB',
    hover: '#1D4ED8',
    font_family: 'Inter, sans-serif',
    border_radius: '12px'
  });

  // State: Portal & Identity Info
  const [portalInfo, setPortalInfo] = useState({
    portal_name: 'MarkazSchool Pro Portal',
    custom_domain: '',
    email_sender_name: 'MarkazSchool Pro Alerts',
    email_logo_enabled: true,
    sms_signature: '- MarkazSchool Pro',
    login_page_background: ''
  });

  // State: Templates
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedEmailType, setSelectedEmailType] = useState('welcome');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  const [smsTemplates, setSmsTemplates] = useState([]);
  const [selectedSmsType, setSelectedSmsType] = useState('attendance');
  const [smsText, setSmsText] = useState('');

  const [certDesign, setCertDesign] = useState({
    title: 'Certificate of Achievement',
    border_style: 'golden_frame',
    show_logo: true,
    show_stamp: true,
    show_signature: true,
    body_text: 'This is proudly presented to {{student_name}} for outstanding performance in {{subject}}.',
    footer_text: 'Official School Certificate'
  });

  // Fetch all branding data on load
  const fetchBrandingData = async () => {
    try {
      setLoading(true);
      const [configRes, emailRes, smsRes, certRes] = await Promise.all([
        api.get('/api/v1/branding/config').catch(() => ({ data: null })),
        api.get('/api/v1/branding/email-templates').catch(() => ({ data: null })),
        api.get('/api/v1/branding/sms-templates').catch(() => ({ data: null })),
        api.get('/api/v1/branding/certificate-template').catch(() => ({ data: null }))
      ]);

      if (configRes.data?.success && configRes.data.data) {
        const d = configRes.data.data;
        if (d.colors) setColors((prev) => ({ ...prev, ...d.colors, ...d.typography }));
        setPortalInfo({
          portal_name: d.portal_name || 'MarkazSchool Pro Portal',
          custom_domain: d.custom_domain || '',
          email_sender_name: d.email_sender_name || 'MarkazSchool Pro Alerts',
          email_logo_enabled: d.email_logo_enabled !== false,
          sms_signature: d.sms_signature || '- MarkazSchool Pro',
          login_page_background: d.login_page_background || ''
        });
      }

      if (emailRes.data?.success && Array.isArray(emailRes.data.data)) {
        setEmailTemplates(emailRes.data.data);
        const welcomeT = emailRes.data.data.find(t => t.template_type === 'welcome') || emailRes.data.data[0];
        if (welcomeT) {
          setSelectedEmailType(welcomeT.template_type);
          setEmailSubject(welcomeT.subject || '');
          setEmailBody(welcomeT.template_html || '');
        }
      }

      if (smsRes.data?.success && Array.isArray(smsRes.data.data)) {
        setSmsTemplates(smsRes.data.data);
        const attSms = smsRes.data.data.find(t => t.template_type === 'attendance') || smsRes.data.data[0];
        if (attSms) {
          setSelectedSmsType(attSms.template_type);
          setSmsText(attSms.template_text || '');
        }
      }

      if (certRes.data?.success && certRes.data.data?.template_design) {
        try {
          const parsed = typeof certRes.data.data.template_design === 'string'
            ? JSON.parse(certRes.data.data.template_design)
            : certRes.data.data.template_design;
          setCertDesign((prev) => ({ ...prev, ...parsed }));
        } catch (e) {
          // fallback default
        }
      }
    } catch (err) {
      console.error('Error fetching branding data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrandingData();
  }, []);

  // Save Colors & Theme (0ms Instant & Non-blocking)
  const handleSaveColors = async () => {
    // 1. Optimistically apply CSS variables & font family instantly
    if (colors.primary) document.documentElement.style.setProperty('--primary-color', colors.primary);
    if (colors.secondary) document.documentElement.style.setProperty('--secondary-color', colors.secondary);
    if (colors.tertiary) document.documentElement.style.setProperty('--tertiary-color', colors.tertiary);
    if (colors.button) document.documentElement.style.setProperty('--button-color', colors.button);
    if (colors.font_family) {
      document.documentElement.style.setProperty('--font-family', colors.font_family);
      document.body.style.fontFamily = colors.font_family;
    }

    // 2. Display success feedback banner instantly
    setFeedback({ type: 'success', message: 'Brand colors & theme updated successfully!' });

    // 3. Sync to MySQL database in background
    try {
      const res = await api.post('/api/v1/branding/colors', colors);
      if (res.data?.success && reloadSchoolSettings) {
        reloadSchoolSettings();
      }
    } catch (err) {
      setFeedback({ type: 'error', message: err.response?.data?.message || 'Failed to sync color theme.' });
    }
  };

  // Save Portal Info (0ms Instant)
  const handleSavePortalInfo = async () => {
    setFeedback({ type: 'success', message: 'Portal info & white-label settings saved!' });
    try {
      await api.post('/api/v1/branding/portal-info', portalInfo);
      if (reloadSchoolSettings) reloadSchoolSettings();
    } catch (err) {
      setFeedback({ type: 'error', message: err.response?.data?.message || 'Failed to save portal info.' });
    }
  };

  // Save Email Template (0ms Instant)
  const handleSaveEmailTemplate = async () => {
    setFeedback({ type: 'success', message: `Email template '${selectedEmailType}' saved!` });
    try {
      await api.post('/api/v1/branding/email-templates', {
        template_type: selectedEmailType,
        subject: emailSubject,
        template_html: emailBody
      });
    } catch (err) {
      setFeedback({ type: 'error', message: 'Failed to save email template.' });
    }
  };

  // Save SMS Template (0ms Instant)
  const handleSaveSmsTemplate = async () => {
    setFeedback({ type: 'success', message: `SMS template '${selectedSmsType}' saved!` });
    try {
      await api.post('/api/v1/branding/sms-templates', {
        template_type: selectedSmsType,
        template_text: smsText
      });
    } catch (err) {
      setFeedback({ type: 'error', message: 'Failed to save SMS template.' });
    }
  };

  // Save Certificate Designer (0ms Instant)
  const handleSaveCertificate = async () => {
    setFeedback({ type: 'success', message: 'Certificate template design saved successfully!' });
    try {
      await api.post('/api/v1/branding/certificate-template', {
        certificate_type: 'achievement',
        template_design: certDesign
      });
    } catch (err) {
      setFeedback({ type: 'error', message: 'Failed to save certificate template.' });
    }
  };

  const tabs = [
    { id: 'colors', label: '🎨 Colors & Theme' },
    { id: 'media', label: '🖼️ Logo & Media' },
    { id: 'email', label: '✉️ Email Customization' },
    { id: 'sms', label: '📱 SMS Customization' },
    { id: 'login', label: '🔐 Login Page' },
    { id: 'portal', label: '🏷️ Portal & Domain' },
    { id: 'cert', label: '🎓 Certificates' },
    { id: 'notifications', label: '🔔 Notifications' }
  ];

  const content = (
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Header Title */}
        {!embedded && (
          <div className="bg-gradient-to-r from-slate-900 via-purple-950 to-indigo-900 rounded-3xl p-6 text-white shadow-xl flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <span className="px-2.5 py-0.5 bg-amber-500/20 text-amber-300 text-[10px] font-extrabold rounded-full border border-amber-500/30 uppercase tracking-widest">
                  Executive Control Center
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                ⚙️ White-Label & Brand Management
              </h1>
              <p className="text-slate-300 text-xs sm:text-sm mt-1">
                Customize portal theme, emails, SMS, login page, and certificates for your school identity.
              </p>
            </div>

            <div className="bg-slate-800/80 border border-slate-700 px-4 py-2 rounded-2xl text-xs font-mono text-slate-300">
              Engine: <strong className="text-amber-400">MarkazSchool Pro</strong> by Waleed Tech
            </div>
          </div>
        )}

        {/* Feedback Alert */}
        {feedback.message && (
          <div className={`p-4 rounded-xl text-sm border flex items-center justify-between shadow-md ${
            feedback.type === 'success' ? 'bg-emerald-950/90 border-emerald-700 text-emerald-300' : 'bg-rose-950/90 border-rose-700 text-rose-300'
          }`}>
            <span>{feedback.message}</span>
            <button onClick={() => setFeedback({ type: '', message: '' })} className="font-bold text-lg leading-none">&times;</button>
          </div>
        )}

        {/* Executive 8-Tab Navigation Bar */}
        <div className="bg-white rounded-2xl p-2 border border-slate-200 shadow-sm flex flex-wrap gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 rounded-xl text-xs font-extrabold transition flex items-center space-x-1.5 ${
                activeTab === tab.id
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* TAB 1: Colors & Theme */}
        {activeTab === 'colors' && (
          <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-6">
            <h3 className="text-base font-extrabold text-slate-900 border-b pb-3 flex items-center justify-between">
              <span>🎨 Theme Color Palette Tokens</span>
              <span className="text-xs text-purple-600 font-bold">Real-Time Global System Theme</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Primary Color (Navbars/Banners)</label>
                <div className="flex items-center space-x-2">
                  <input type="color" value={colors.primary} onChange={(e) => setColors({ ...colors, primary: e.target.value })} className="h-10 w-12 rounded cursor-pointer border" />
                  <input type="text" value={colors.primary} onChange={(e) => setColors({ ...colors, primary: e.target.value })} className="w-full bg-slate-50 border rounded-lg px-3 py-2 text-xs font-mono font-bold" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Secondary Color (Cards/Headers)</label>
                <div className="flex items-center space-x-2">
                  <input type="color" value={colors.secondary} onChange={(e) => setColors({ ...colors, secondary: e.target.value })} className="h-10 w-12 rounded cursor-pointer border" />
                  <input type="text" value={colors.secondary} onChange={(e) => setColors({ ...colors, secondary: e.target.value })} className="w-full bg-slate-50 border rounded-lg px-3 py-2 text-xs font-mono font-bold" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Tertiary Accent (Green Badges)</label>
                <div className="flex items-center space-x-2">
                  <input type="color" value={colors.tertiary} onChange={(e) => setColors({ ...colors, tertiary: e.target.value })} className="h-10 w-12 rounded cursor-pointer border" />
                  <input type="text" value={colors.tertiary} onChange={(e) => setColors({ ...colors, tertiary: e.target.value })} className="w-full bg-slate-50 border rounded-lg px-3 py-2 text-xs font-mono font-bold" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Button Action Color</label>
                <div className="flex items-center space-x-2">
                  <input type="color" value={colors.button} onChange={(e) => setColors({ ...colors, button: e.target.value })} className="h-10 w-12 rounded cursor-pointer border" />
                  <input type="text" value={colors.button} onChange={(e) => setColors({ ...colors, button: e.target.value })} className="w-full bg-slate-50 border rounded-lg px-3 py-2 text-xs font-mono font-bold" />
                </div>
              </div>
            </div>

            {/* Typography & Border Radius */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Font Family</label>
                <select value={colors.font_family} onChange={(e) => setColors({ ...colors, font_family: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold">
                  <option value="Inter, sans-serif">Inter (Modern Clean)</option>
                  <option value="'Plus Jakarta Sans', sans-serif">Plus Jakarta Sans (Executive)</option>
                  <option value="Roboto, sans-serif">Roboto (Standard)</option>
                  <option value="'Noto Nastaliq Urdu', serif">Noto Nastaliq Urdu (Urdu Native)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">UI Border Radius</label>
                <select value={colors.border_radius} onChange={(e) => setColors({ ...colors, border_radius: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold">
                  <option value="8px">8px (Slight Curved)</option>
                  <option value="12px">12px (Modern Standard)</option>
                  <option value="18px">18px (Soft Rounded)</option>
                  <option value="24px">24px (Pill Shaped)</option>
                </select>
              </div>
            </div>

            {/* Live Interactive Preview Box */}
            <div className="p-5 rounded-2xl border space-y-3" style={{ backgroundColor: colors.primary, color: 'white', borderRadius: colors.border_radius }}>
              <div className="flex items-center justify-between">
                <span className="font-extrabold text-sm">Theme Live Preview Component</span>
                <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: colors.tertiary, color: '#0F172A' }}>Active Status</span>
              </div>
              <p className="text-xs opacity-80">This is how your school custom branding buttons and headers will appear across all user portals.</p>
              <button style={{ backgroundColor: colors.button, color: 'white', borderRadius: colors.border_radius }} className="px-4 py-2 text-xs font-bold shadow-md">
                Sample Primary Action Button
              </button>
            </div>

            <div className="flex justify-end pt-2">
              <button onClick={handleSaveColors} disabled={saving} className="bg-purple-600 hover:bg-purple-500 text-white font-extrabold text-xs px-6 py-3 rounded-xl shadow-md transition disabled:opacity-50">
                {saving ? 'Saving Colors...' : 'Save Color Theme'}
              </button>
            </div>
          </div>
        )}

        {/* TAB 2: Logo & Media */}
        {activeTab === 'media' && (
          <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-6">
            <h3 className="text-base font-extrabold text-slate-900 border-b pb-3">🖼️ Logos, Stamp & Signature Media Assets</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {/* Logo Upload Box */}
              <div className="border border-slate-200 p-5 rounded-2xl text-center space-y-3 bg-slate-50 flex flex-col items-center justify-between">
                <div className="w-16 h-16 rounded-xl border border-slate-200 bg-white p-1 flex items-center justify-center overflow-hidden">
                  {isValidImage(school?.logo) ? (
                    <img
                      src={getImageUrl(school.logo)}
                      alt="School Logo"
                      className="max-h-full max-w-full object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="text-2xl">🏫</span>
                  )}
                </div>
                <div>
                  <h4 className="font-bold text-xs text-slate-900">School Main Logo</h4>
                  <p className="text-[11px] text-slate-500 mt-0.5">Displayed in Navbar, Reports & Cards (SVG, PNG, JPG)</p>
                </div>
                <input
                  type="file"
                  ref={logoInputRef}
                  onChange={handleUploadLogo}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => logoInputRef.current?.click()}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-xl shadow-xs transition disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Upload Logo File'}
                </button>
              </div>

              {/* Official Stamp Upload Box */}
              <div className="border border-slate-200 p-5 rounded-2xl text-center space-y-3 bg-slate-50 flex flex-col items-center justify-between">
                <div className="w-16 h-16 rounded-xl border border-slate-200 bg-white p-1 flex items-center justify-center overflow-hidden">
                  {isValidImage(school?.stamp) ? (
                    <img
                      src={getImageUrl(school.stamp)}
                      alt="Official Stamp"
                      className="max-h-full max-w-full object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="text-2xl">🔖</span>
                  )}
                </div>
                <div>
                  <h4 className="font-bold text-xs text-slate-900">Official School Stamp</h4>
                  <p className="text-[11px] text-slate-500 mt-0.5">Printed on Fee Receipts & Official Documents</p>
                </div>
                <input
                  type="file"
                  ref={stampInputRef}
                  onChange={handleUploadStamp}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => stampInputRef.current?.click()}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-xl shadow-xs transition disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Upload Stamp File'}
                </button>
              </div>

              {/* Principal Signature Box */}
              <div className="border border-slate-200 p-5 rounded-2xl text-center space-y-3 bg-slate-50 flex flex-col items-center justify-between">
                <div className="w-16 h-16 rounded-xl border border-slate-200 bg-white p-1 flex items-center justify-center overflow-hidden">
                  {isValidImage(school?.principal_signature) ? (
                    <img
                      src={getImageUrl(school.principal_signature)}
                      alt="Principal Signature"
                      className="max-h-full max-w-full object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="text-2xl">✍️</span>
                  )}
                </div>
                <div>
                  <h4 className="font-bold text-xs text-slate-900">Principal Signature</h4>
                  <p className="text-[11px] text-slate-500 mt-0.5">Appears on Result Cards & Certificates</p>
                </div>
                <input
                  type="file"
                  ref={sigInputRef}
                  onChange={handleUploadSignature}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => sigInputRef.current?.click()}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-xl shadow-xs transition disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Upload Signature'}
                </button>
              </div>
            </div>

            {/* Bottom Save Action Bar */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-4 border-t border-slate-100">
              <span className="text-xs text-slate-500 font-medium">Changes to media assets take effect across all school portals immediately.</span>
              <button
                type="button"
                onClick={() => setFeedback({ type: 'success', message: 'Media assets configuration saved successfully!' })}
                className="bg-purple-600 hover:bg-purple-500 text-white font-extrabold text-xs px-6 py-3 rounded-xl shadow-md transition shrink-0"
              >
                Save Media Assets
              </button>
            </div>
          </div>
        )}

        {/* TAB 3: Email Customization */}
        {activeTab === 'email' && (
          <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-6">
            <h3 className="text-base font-extrabold text-slate-900 border-b pb-3">✉️ Custom Email Sender & Template Builder</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Email Sender Name</label>
                <input type="text" value={portalInfo.email_sender_name} onChange={(e) => setPortalInfo({ ...portalInfo, email_sender_name: e.target.value })} placeholder="e.g. Bright Future International School" className="w-full bg-slate-50 border rounded-xl px-3 py-2 text-xs font-semibold" />
              </div>

              <div className="flex items-center space-x-3 pt-5">
                <input type="checkbox" id="email-logo-chk" checked={portalInfo.email_logo_enabled} onChange={(e) => setPortalInfo({ ...portalInfo, email_logo_enabled: e.target.checked })} className="h-4 w-4 text-purple-600 rounded" />
                <label htmlFor="email-logo-chk" className="text-xs font-bold text-slate-700 cursor-pointer">Include School Logo in Email Header</label>
              </div>
            </div>

            {/* Email Template Builder */}
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <div className="flex justify-between items-center">
                <h4 className="font-bold text-xs text-slate-900 uppercase">Select Email Template Type:</h4>
                <select value={selectedEmailType} onChange={(e) => setSelectedEmailType(e.target.value)} className="bg-purple-50 border border-purple-200 text-purple-900 text-xs font-bold rounded-xl px-3 py-1.5">
                  <option value="welcome">Welcome Email</option>
                  <option value="result_notification">Exam Result Alert</option>
                  <option value="fee_alert">Fee Payment Notice</option>
                  <option value="attendance_alert">Daily Attendance Notice</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Subject Line</label>
                <input type="text" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} className="w-full bg-slate-50 border rounded-xl px-3 py-2 text-xs font-semibold" />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">HTML Template Body</label>
                <textarea rows="6" value={emailBody} onChange={(e) => setEmailBody(e.target.value)} className="w-full bg-slate-900 text-slate-100 font-mono text-xs p-4 rounded-xl border"></textarea>
                <div className="text-[11px] text-slate-500 mt-1">Available variables: <code className="bg-slate-100 text-purple-700 px-1 py-0.5 rounded">{"{{school_name}}"}</code>, <code className="bg-slate-100 text-purple-700 px-1 py-0.5 rounded">{"{{student_name}}"}</code>, <code className="bg-slate-100 text-purple-700 px-1 py-0.5 rounded">{"{{date}}"}</code>, <code className="bg-slate-100 text-purple-700 px-1 py-0.5 rounded">{"{{subject}}"}</code></div>
              </div>

              <div className="flex justify-end">
                <button onClick={handleSaveEmailTemplate} disabled={saving} className="bg-purple-600 text-white text-xs font-extrabold px-5 py-2.5 rounded-xl shadow-xs">
                  Save Email Template
                </button>
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: SMS Customization */}
        {activeTab === 'sms' && (
          <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-6">
            <h3 className="text-base font-extrabold text-slate-900 border-b pb-3">📱 Custom SMS Signature & Event Alerts</h3>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Custom SMS Signature (Appended to all SMS)</label>
              <input type="text" value={portalInfo.sms_signature} onChange={(e) => setPortalInfo({ ...portalInfo, sms_signature: e.target.value })} placeholder="e.g. - Bright Future School" className="w-full bg-slate-50 border rounded-xl px-3 py-2 text-xs font-semibold" />
            </div>

            <div className="space-y-4 pt-4 border-t border-slate-100">
              <div className="flex justify-between items-center">
                <h4 className="font-bold text-xs text-slate-900 uppercase">SMS Event Type:</h4>
                <select value={selectedSmsType} onChange={(e) => setSelectedSmsType(e.target.value)} className="bg-purple-50 border text-purple-900 text-xs font-bold rounded-xl px-3 py-1.5">
                  <option value="attendance">Attendance Alert</option>
                  <option value="result">Exam Result Alert</option>
                  <option value="fee_due">Fee Payment Due</option>
                  <option value="test_reminder">Test Reminder</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">SMS Message Text</label>
                <textarea rows="3" value={smsText} onChange={(e) => setSmsText(e.target.value)} className="w-full bg-slate-50 border rounded-xl p-3 text-xs font-mono"></textarea>
                <div className="text-[11px] text-slate-500 mt-1 flex justify-between">
                  <span>Variables: <code className="bg-slate-100 text-purple-700 px-1 py-0.5 rounded">{"{{student_name}}"}</code>, <code className="bg-slate-100 text-purple-700 px-1 py-0.5 rounded">{"{{school_name}}"}</code>, <code className="bg-slate-100 text-purple-700 px-1 py-0.5 rounded">{"{{marks}}"}</code></span>
                  <span>Char Count: {smsText.length} / 160</span>
                </div>
              </div>

              <div className="flex justify-end">
                <button onClick={handleSaveSmsTemplate} disabled={saving} className="bg-purple-600 text-white text-xs font-extrabold px-5 py-2.5 rounded-xl shadow-xs">
                  Save SMS Template
                </button>
              </div>
            </div>
          </div>
        )}

        {/* TAB 5: Login Page Branding */}
        {activeTab === 'login' && (
          <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-6">
            <h3 className="text-base font-extrabold text-slate-900 border-b pb-3">🔐 Login Page Branding & Welcome Message</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Background Image URL</label>
                <input type="text" value={portalInfo.login_page_background} onChange={(e) => setPortalInfo({ ...portalInfo, login_page_background: e.target.value })} placeholder="https://example.com/school-campus-bg.jpg" className="w-full bg-slate-50 border rounded-xl px-3 py-2 text-xs font-semibold" />
              </div>

              <div className="p-4 bg-slate-900 rounded-2xl text-white space-y-2 text-xs">
                <div className="font-bold text-amber-400">Login Screen Live Preview:</div>
                <div className="p-4 border border-slate-700 rounded-xl bg-slate-800 text-center space-y-2">
                  <div className="text-lg font-extrabold">{portalInfo.portal_name || 'MarkazSchool Pro Portal'}</div>
                  <div className="text-xs text-slate-400">Welcome to official school management portal</div>
                </div>
              </div>

              <div className="flex justify-end">
                <button onClick={handleSavePortalInfo} disabled={saving} className="bg-purple-600 text-white text-xs font-extrabold px-5 py-2.5 rounded-xl shadow-xs">
                  Save Login Page Branding
                </button>
              </div>
            </div>
          </div>
        )}

        {/* TAB 6: Portal Name & Custom Domain */}
        {activeTab === 'portal' && (
          <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-6">
            <h3 className="text-base font-extrabold text-slate-900 border-b pb-3">🏷️ Portal Name Customization & Domain Mapping</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Portal Name (Global Title)</label>
                <input type="text" value={portalInfo.portal_name} onChange={(e) => setPortalInfo({ ...portalInfo, portal_name: e.target.value })} placeholder="e.g. Student Portal or Bright Future Portal" className="w-full bg-slate-50 border rounded-xl px-3 py-2 text-xs font-extrabold text-slate-900" />
                <div className="text-[11px] text-slate-500 mt-1">Replaces system brand name across browser tabs, dashboards, and footers.</div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Custom School Domain</label>
                <input type="text" value={portalInfo.custom_domain} onChange={(e) => setPortalInfo({ ...portalInfo, custom_domain: e.target.value })} placeholder="e.g. portal.school.edu.pk" className="w-full bg-slate-50 border rounded-xl px-3 py-2 text-xs font-mono font-bold" />
              </div>

              <div className="p-4 bg-purple-50 border border-purple-100 rounded-2xl text-xs text-purple-900 space-y-1.5">
                <div className="font-bold">🌐 Custom Domain Setup Instructions:</div>
                <div>Add a CNAME record in your domain DNS control panel:</div>
                <code className="bg-purple-100 px-2 py-1 rounded font-mono block text-[11px]">CNAME portal.school.edu.pk ➔ app.markazschool.com</code>
              </div>

              <div className="flex justify-end">
                <button onClick={handleSavePortalInfo} disabled={saving} className="bg-purple-600 text-white text-xs font-extrabold px-5 py-2.5 rounded-xl shadow-xs">
                  Save Portal Name & Domain
                </button>
              </div>
            </div>
          </div>
        )}

        {/* TAB 7: Certificates */}
        {activeTab === 'cert' && (
          <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-6">
            <h3 className="text-base font-extrabold text-slate-900 border-b pb-3">🎓 Certificate Layout & Seal Designer</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Certificate Header Title</label>
                <input type="text" value={certDesign.title} onChange={(e) => setCertDesign({ ...certDesign, title: e.target.value })} className="w-full bg-slate-50 border rounded-xl px-3 py-2 text-xs font-bold" />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Certificate Body Wording</label>
                <textarea rows="3" value={certDesign.body_text} onChange={(e) => setCertDesign({ ...certDesign, body_text: e.target.value })} className="w-full bg-slate-50 border rounded-xl p-3 text-xs font-mono"></textarea>
              </div>

              <div className="flex flex-wrap gap-4 text-xs font-bold text-slate-700">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" checked={certDesign.show_logo} onChange={(e) => setCertDesign({ ...certDesign, show_logo: e.target.checked })} />
                  <span>Show School Logo</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" checked={certDesign.show_stamp} onChange={(e) => setCertDesign({ ...certDesign, show_stamp: e.target.checked })} />
                  <span>Show School Official Stamp</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" checked={certDesign.show_signature} onChange={(e) => setCertDesign({ ...certDesign, show_signature: e.target.checked })} />
                  <span>Show Principal Signature Line</span>
                </label>
              </div>

              <div className="flex justify-end">
                <button onClick={handleSaveCertificate} disabled={saving} className="bg-purple-600 text-white text-xs font-extrabold px-5 py-2.5 rounded-xl shadow-xs">
                  Save Certificate Layout
                </button>
              </div>
            </div>
          </div>
        )}

        {/* TAB 8: Notifications */}
        {activeTab === 'notifications' && (
          <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-6">
            <h3 className="text-base font-extrabold text-slate-900 border-b pb-3">🔔 Notification Alerts & Templates</h3>
            <p className="text-xs text-slate-500">Configure in-app alert banners and mobile push notification branding for teachers, parents, and students.</p>
            <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-2xl text-xs font-medium">
              ✅ All in-app notifications automatically inherit your brand's primary and accent colors.
            </div>
          </div>
        )}

      </div>
  );

  return embedded ? content : <DashboardLayout>{content}</DashboardLayout>;
};

export default BrandingSettings;
